import { supabase } from "./supabase";
import type { AulaBonus } from "./aulasBonus";

export type { AulaBonus } from "./aulasBonus";

// O IO dos cursos bônus da Pós Finanças: a lista de aulas de um card (000177) e
// o progresso do aluno nelas (000183) — concluir e anotar.
//
// ---------------------------------------------------------------------------
// Zero regra aqui
// ---------------------------------------------------------------------------
// Quem decide se o aluno pode ver a lista, concluir ou anotar é o banco —
// `bonus_minhas_aulas`, `bonus_concluir_aula` e `bonus_anotar_aula`, que
// perguntam a `abrir_card`, o mesmo portão da vitrine e da ponte `/abrir`. Este
// arquivo lê, grava e tipa. A REGRA de apresentação (quantas de quantas, qual é a
// próxima) mora em `lib/aulasBonus.ts`, que é puro e tem prova própria.
//
// E as tabelas `bonus_aulas` e `bonus_progresso` NÃO são legíveis pela Data API
// (`revoke all`): a lista de aulas de um produto pago é conteúdo de produto, e o
// progresso é dado do aluno. Não existe caminho de `select` cru para tentar — é a
// lição da 000136, onde a tela do aluno usava `select` numa tabela cuja policy
// tem `or eh_suporte()` e a conta de admin recebeu os 6.338 emitidos de todo
// mundo.

export type CursoBonus = {
  card: string;
  curso: string;
  /** O nome do curso COMO O MEMBERKIT o escreve, para a tela poder citá-lo. */
  nomeNaFonte: string;
  sincronizadoEm: string | null;
  aulas: AulaBonus[];
};

export type LeituraBonus =
  | { fase: "pronto"; curso: CursoBonus }
  | { fase: "recusado"; motivo: string }
  | { fase: "erro" };

/**
 * As aulas de um curso bônus, na ordem em que a tela as mostra, com o progresso
 * do aluno em cada uma.
 *
 * A ordenação vem do BANCO (seção, ordem, id — com desempate estável, a cicatriz
 * da `vw_contrato_a_assinar`) e não é refeita aqui: reordenar em JavaScript
 * criaria uma segunda ordem, e a que divergisse em silêncio seria justamente a
 * que ninguém compara.
 */
export async function lerAulasDoBonus(card: string, curso: string): Promise<LeituraBonus> {
  const { data, error } = await supabase.rpc("bonus_minhas_aulas", {
    p_card: card,
    p_curso: curso,
  });

  if (error) return { fase: "erro" };

  const r = (data ?? {}) as {
    ok?: boolean;
    motivo?: string;
    card?: string;
    curso?: string;
    nome_na_fonte?: string;
    sincronizado_em?: string | null;
    aulas?: unknown;
  };

  // `ok: false` é RECUSA com motivo — sem acesso, contrato pendente, catálogo por
  // sincronizar. A tela traduz cada um (`mensagemDaAbertura`), porque as ações
  // são diferentes: assinar o contrato é coisa que o aluno resolve; "estamos
  // cadastrando" é esperar.
  if (r.ok !== true) return { fase: "recusado", motivo: r.motivo ?? "sem-acesso" };

  const lista = Array.isArray(r.aulas) ? r.aulas : [];
  return {
    fase: "pronto",
    curso: {
      card: r.card ?? card,
      curso: r.curso ?? curso,
      nomeNaFonte: r.nome_na_fonte ?? "",
      sincronizadoEm: r.sincronizado_em ?? null,
      aulas: lista.map((bruta) => {
        const a = bruta as Record<string, unknown>;
        return {
          aulaId: Number(a.aula_id),
          titulo: String(a.titulo ?? ""),
          secao: typeof a.secao === "string" && a.secao !== "" ? a.secao : null,
          ordem: Number(a.ordem),
          duracaoSegundos: typeof a.duracao === "number" ? a.duracao : null,
          temMaterial: a.material === true,
          // Os três fatos do progresso. `null` no JSON é "não fez", e a chave
          // existe sempre (o caso 15 do teste 65 cobra) — a tela nunca precisa
          // distinguir "não concluída" de "campo que sumiu".
          concluidaEm: typeof a.concluida_em === "string" ? a.concluida_em : null,
          abertaEm: typeof a.aberta_em === "string" ? a.aberta_em : null,
          anotacao: typeof a.anotacao === "string" && a.anotacao !== "" ? a.anotacao : null,
        };
      }),
    },
  };
}

/** O resultado de uma escrita: o que o banco gravou, ou a recusa com motivo. */
export type Escrita<T> = ({ ok: true } & T) | { ok: false; motivo: string };

/**
 * Marca (ou desmarca) uma aula como concluída.
 *
 * Devolve a data que o SERVIDOR gravou — é ela que a tela mostra em "concluída
 * em", nunca `new Date()` do navegador. E marcar o que já está marcado devolve a
 * data ANTIGA: a RPC é idempotente por estado, então o duplo clique não
 * reescreve a data.
 */
export async function concluirAula(
  card: string,
  curso: string,
  aulaId: number,
  concluida: boolean,
): Promise<Escrita<{ concluidaEm: string | null }>> {
  const { data, error } = await supabase.rpc("bonus_concluir_aula", {
    p_card: card,
    p_curso: curso,
    p_aula: aulaId,
    p_concluida: concluida,
  });
  if (error) return { ok: false, motivo: "falha" };
  const r = (data ?? {}) as { ok?: boolean; motivo?: string; concluida_em?: unknown };
  if (r.ok !== true) return { ok: false, motivo: r.motivo ?? "falha" };
  return { ok: true, concluidaEm: typeof r.concluida_em === "string" ? r.concluida_em : null };
}

/**
 * Guarda o bilhete do aluno sobre a aula. Texto vazio APAGA.
 *
 * O texto volta como o banco o guardou (aparado), para a tela saber se ainda há
 * anotação — e o limite de 10 mil caracteres é recusa com motivo
 * (`anotacao-longa`), não erro de banco.
 */
export async function anotarAula(
  card: string,
  curso: string,
  aulaId: number,
  texto: string,
): Promise<Escrita<{ anotacao: string | null }>> {
  const { data, error } = await supabase.rpc("bonus_anotar_aula", {
    p_card: card,
    p_curso: curso,
    p_aula: aulaId,
    p_texto: texto,
  });
  if (error) return { ok: false, motivo: "falha" };
  const r = (data ?? {}) as { ok?: boolean; motivo?: string; anotacao?: unknown };
  if (r.ok !== true) return { ok: false, motivo: r.motivo ?? "falha" };
  return {
    ok: true,
    anotacao: typeof r.anotacao === "string" && r.anotacao !== "" ? r.anotacao : null,
  };
}

/**
 * A frase para o aluno quando UMA escrita não gravou.
 *
 * Erro de ESCRITA não derruba a tela: a peça que falhou informa, e a mensagem diz
 * que o resto continua ali (a lição do `alternarFeito` do Calendário). Os
 * motivos são os que as RPCs devolvem; qualquer outro é falha nossa.
 */
export function mensagemDaEscrita(motivo: string): string {
  switch (motivo) {
    case "sem-acesso":
    case "trancado":
    case "sem-perfil":
      return "Seu acesso a este curso não está ativo agora, então não dá para marcar. As aulas continuam aqui.";
    case "aula-desconhecida":
    case "curso-bonus-nao-cadastrado":
      return "Esta aula mudou de lugar. Atualize a página e tente de novo.";
    case "anotacao-longa":
      return "A anotação passou de 10 mil caracteres. Encurte um pouco para salvar.";
    default:
      return "Não conseguimos gravar agora. As aulas continuam aqui — tente de novo.";
  }
}
